'use client';

import { useRealtimeComments } from '@/app/(traffic)/traffic/contexts/RealtimeProvider';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';
import { AnimatePresence, motion } from 'framer-motion';
import { Trash2 } from 'lucide-react';
import { useSession } from 'next-auth/react';
import Image from 'next/image';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { getUserColor } from '../../../lib/utils/getUserColor';
import { CommentData } from '../../../types/shared';
import { TrafficAddCommentForm } from './TrafficAddCommentForm';

type ChildrenMap = Map<string | null, CommentData[]>;

// 🌳 Алгоритм построения дерева за один проход O(N)
function buildCommentTree(comments: CommentData[]) {
  const map: ChildrenMap = new Map();

  comments.forEach((comment) => {
    const parentKey = comment.parentId ?? null;
    const existingChildren = map.get(parentKey) ?? [];
    existingChildren.push(comment);
    map.set(parentKey, existingChildren);
  });

  return { childrenMap: map };
}

// --- Компонент одного комментария (Рекурсивный) ---
const TrafficCommentThread = memo(function TrafficCommentThread({
  comment,
  childrenMap,
  reportId,
  onCommentAdded,
  onDelete,
}: {
  comment: CommentData;
  childrenMap: ChildrenMap;
  reportId: string;
  onCommentAdded: (c: CommentData) => void;
  onDelete: (id: string) => void;
}) {
  const [isReplying, setIsReplying] = useState(false);
  const { data: session } = useSession();
  const isAdmin = session?.user?.role === 'ADMIN';

  // ✅ Мгновенный доступ к детям (O(1))
  const replies = childrenMap.get(comment.id) ?? [];
  const userNameColorStyle = getUserColor(comment.user.id);

  const timeAgo = useMemo(
    () => formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true, locale: ru }),
    [comment.createdAt],
  );

  const handleDeleteClick = useCallback(() => {
    toast((t) => (
      <div className="flex flex-col gap-2">
        <p className="font-semibold text-sm">Удалить комментарий?</p>
        <div className="flex gap-2">
          <button
            onClick={() => {
              toast.dismiss(t.id);
              onDelete(comment.id);
            }}
            className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
          >
            Удалить
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="px-3 py-1 bg-slate-200 text-black rounded text-sm hover:bg-slate-300"
          >
            Отмена
          </button>
        </div>
      </div>
    ));
  }, [comment.id, onDelete]);

  return (
    <article id={`comment-${comment.id}`} className="flex items-start gap-3">
      <Image
        src={comment.user.image || '/default-avatar.jpg'}
        alt={comment.user.name || 'User'}
        width={32}
        height={32}
        className="rounded-full mt-1 flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="bg-slate-800 rounded-lg p-3">
          <p className="font-bold text-sm">
            <span style={userNameColorStyle}>{comment.user.name || 'Аноним'}</span>
          </p>
          <p className="text-slate-200 mt-1 whitespace-pre-wrap text-sm">{comment.text}</p>
        </div>

        <div className="text-xs text-slate-400 flex items-center gap-3 mt-1 pl-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <time className="cursor-help">{timeAgo}</time>
            </TooltipTrigger>
            <TooltipContent>{new Date(comment.createdAt).toLocaleString('ru')}</TooltipContent>
          </Tooltip>

          {session?.user && (
            <button
              onClick={() => setIsReplying(!isReplying)}
              className="hover:text-white transition-colors font-medium"
            >
              {isReplying ? 'Отмена' : 'Ответить'}
            </button>
          )}

          {isAdmin && (
            <button
              onClick={handleDeleteClick}
              className="text-red-500/80 hover:text-red-500 flex items-center gap-1 transition-colors"
            >
              <Trash2 size={12} /> Удалить
            </button>
          )}
        </div>

        <AnimatePresence>
          {isReplying && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-2"
            >
              <TrafficAddCommentForm
                reportId={reportId}
                parentId={comment.id}
                onCommentAdded={(c) => {
                  setIsReplying(false);
                  onCommentAdded(c);
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Рендер детей */}
        {replies.length > 0 && (
          <div className="mt-3 space-y-3 pl-3 sm:pl-4 border-l-2 border-slate-700">
            {replies.map((reply) => (
              <TrafficCommentThread
                key={reply.id}
                comment={reply}
                childrenMap={childrenMap}
                reportId={reportId}
                onCommentAdded={onCommentAdded}
                onDelete={onDelete}
              />
            ))}
          </div>
        )}
      </div>
    </article>
  );
});

// --- Главный компонент секции ---
export function TrafficCommentsSection({ reportId }: { reportId: string }) {
  const { comments, addOptimisticComment, deleteOptimisticComment } = useRealtimeComments();
  const { data: session } = useSession();
  const [newCommentId, setNewCommentId] = useState<string | null>(null);

  // ✅ Быстрое построение дерева O(N) без sort/copy
  const childrenMap = useMemo(() => {
    const map: ChildrenMap = new Map();
    for (const c of comments) {
      const key = c.parentId ?? null;
      const list = map.get(key);
      if (list) list.push(c);
      else map.set(key, [c]);
    }
    return map;
  }, [comments]);

  const topLevelComments = useMemo(() => childrenMap.get(null) ?? [], [childrenMap]);

  // ✅ Скролл к новому комменту + очистка таймера
  const scrollTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!newCommentId) return;

    if (scrollTimerRef.current) window.clearTimeout(scrollTimerRef.current);

    scrollTimerRef.current = window.setTimeout(() => {
      const el = document.getElementById(`comment-${newCommentId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('highlight-comment');
        window.setTimeout(() => el.classList.remove('highlight-comment'), 2000);
      }
      setNewCommentId(null);
    }, 100);

    return () => {
      if (scrollTimerRef.current) window.clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = null;
    };
  }, [newCommentId]);

  const handleCommentAdded = useCallback(
    (c: CommentData) => {
      setNewCommentId(c.id);
      addOptimisticComment(c);
    },
    [addOptimisticComment],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      deleteOptimisticComment(id);

      try {
        await fetch('/api/traffic/comments/delete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ commentId: id }),
        });
      } catch {
        toast.error('Ошибка удаления');
      }
    },
    [deleteOptimisticComment],
  );

  return (
    <TooltipProvider delayDuration={200}>
      <section className="space-y-4">
        <h3 className="font-bold text-lg text-white">
          Обсуждение <span className="text-slate-400 text-base">({comments.length})</span>
        </h3>

        {session?.user ? (
          <div className="my-4">
            <TrafficAddCommentForm reportId={reportId} onCommentAdded={handleCommentAdded} />
          </div>
        ) : (
          <div className="p-4 bg-slate-800/50 rounded-lg text-center text-sm text-slate-300">
            <a href="/login" className="text-sky-400 hover:underline font-medium">
              Войдите
            </a>
            , чтобы оставить комментарий.
          </div>
        )}

        <div className="space-y-4">
          {topLevelComments.length > 0 ? (
            topLevelComments.map((c) => (
              <TrafficCommentThread
                key={c.id}
                comment={c}
                childrenMap={childrenMap}
                reportId={reportId}
                onCommentAdded={handleCommentAdded}
                onDelete={handleDelete}
              />
            ))
          ) : (
            <p className="text-center text-slate-500 py-4">Комментариев пока нет</p>
          )}
        </div>
      </section>
    </TooltipProvider>
  );
}

